import { CheckCircle2, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import type { Provider } from '../types';

interface ConnectionSummaryProps {
	provider: Provider;
	onBack: () => void;
	onClose: () => void;
}

export function ConnectionSummary({
	provider,
	onBack,
	onClose,
}: ConnectionSummaryProps) {
	const [testState, setTestState] = useState<'idle' | 'loading' | 'ok'>('idle');

	const rows = [
		['Provider', provider.name],
		['Channel type', provider.type],
		['Label', 'Transactional'],
		['Credential key', '•••• saved securely'],
		['From email', 'noreply@fyscaltech.com'],
		['Region', 'Global'],
		['Environment', 'Production'],
		['Status', 'Active'],
	];

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3 rounded-lg border bg-muted/20 p-3">
				<span className="flex h-8 w-8 items-center justify-center rounded bg-muted text-xs font-bold">
					{provider.logo}
				</span>
				<div>
					<p className="font-medium">{provider.name}</p>
					<p className="text-xs text-muted-foreground">{provider.desc}</p>
				</div>
			</div>

			<div className="space-y-1">
				{rows.map(([key, value]) => (
					<div key={key} className="flex items-center border-b py-2.5 text-sm">
						<span className="w-40 text-muted-foreground">{key}</span>
						<span>{value}</span>
					</div>
				))}
			</div>

			{testState === 'loading' && (
				<div className="flex items-center gap-2 rounded border bg-muted/30 p-3 text-sm">
					<Loader2 className="h-4 w-4 animate-spin" />
					Testing connection to {provider.name}...
				</div>
			)}
			{testState === 'ok' && (
				<div className="flex items-center gap-2 rounded border bg-green-50 p-3 text-sm text-green-700">
					<CheckCircle2 className="h-4 w-4" />
					Connection test successful — credentials are valid.
				</div>
			)}

			<div className="flex justify-between">
				<Button variant="ghost" onClick={onBack}>
					← Back
				</Button>
				<Button
					onClick={() => {
						setTestState('loading');
						setTimeout(() => {
							setTestState('ok');
							setTimeout(onClose, 1200);
						}, 1400);
					}}
				>
					⚡ Test Connection
				</Button>
			</div>
		</div>
	);
}
